import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WishlistContext } from '../context/WishlistContext';
import { API_URL } from '../config';
import { getValidImageUrl } from '../utils/getImageUrl';

const MarketplaceCard = ({ product }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useContext(WishlistContext);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="glass-card rounded-xl overflow-hidden gold-glow group transition-all duration-300 flex flex-col border-t-2 border-t-primary/20"
    >
      <div className="relative aspect-square w-full overflow-hidden border-b border-white/5 bg-surface-container-low">
        {/* Wishlist Button */}
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isInWishlist(product.id)) {
              removeFromWishlist(product.id);
            } else {
              addToWishlist(product.id, {
                name: product.name,
                price: product.price,
                newPrice: product.newPrice,
                image: product.image
              });
            }
          }}
          className={`absolute top-4 left-4 z-10 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur transition-all ${isInWishlist(product.id) ? 'bg-primary/20 text-primary border border-primary/50' : 'bg-black/40 text-white border border-white/20 hover:bg-black/70'}`}
          aria-label="Toggle Wishlist"
        >
          <span className={`material-symbols-outlined text-[16px] ${isInWishlist(product.id) ? 'filled' : ''}`}>favorite</span>
        </button>

        {/* Skeleton Loader */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center animate-pulse">
            <span className="material-symbols-outlined text-white/10 text-5xl">image</span>
          </div>
        )}
        
        <img 
          className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} 
          src={product.image ? getValidImageUrl(product.image) : `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect width="400" height="400" fill="%231a1e23"/><rect width="360" height="360" x="20" y="20" fill="%231a1e23" stroke="%23f0b90b" stroke-width="2" stroke-opacity="0.2" rx="20"/><path d="M200 160 l40 60 h-80 z" fill="%23f0b90b" opacity="0.5"/><circle cx="240" cy="140" r="15" fill="%23f0b90b" opacity="0.5"/><text x="50%" y="260" font-family="sans-serif" font-size="16" fill="%23f0b90b" opacity="0.5" dominant-baseline="middle" text-anchor="middle">NO IMAGE PROVIDED</text></svg>`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.target.onerror = null; // Prevent infinite loop
            e.target.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect width="400" height="400" fill="%231a1e23"/><rect width="360" height="360" x="20" y="20" fill="%231a1e23" stroke="%23f0b90b" stroke-width="2" stroke-opacity="0.2" rx="20"/><path d="M200 160 l40 60 h-80 z" fill="%23f0b90b" opacity="0.5"/><circle cx="240" cy="140" r="15" fill="%23f0b90b" opacity="0.5"/><text x="50%" y="260" font-family="sans-serif" font-size="16" fill="%23f0b90b" opacity="0.5" dominant-baseline="middle" text-anchor="middle">IMAGE UNAVAILABLE</text></svg>`;
            setImageLoaded(true);
          }}
          alt={`${product.name || 'EA'} Logo`}
        />
        {product.badge && product.badge !== 'None' && (
          <div className="absolute top-4 right-4 bg-primary text-on-primary text-[10px] font-bold px-2 py-1 rounded tracking-widest uppercase shadow-lg">
            {product.badge}
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-headline-sm text-headline-sm text-silver-text group-hover:text-primary transition-colors">{product.name}</h3>
          <div className="flex flex-col items-end gap-1">
            <span className="bg-white/5 px-2 py-1 rounded text-[10px] font-bold text-on-surface-variant border border-white/5">
              {product.platform || 'MT4/MT5'}
            </span>
            <span className="text-[10px] text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
              {product.category || 'Expert Advisor'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 mb-4">
          <span className="material-symbols-outlined text-primary text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
          <span className="font-label-sm text-label-sm text-silver-text">4.9</span>
          <span className="text-on-surface-variant text-[12px]">(248 reviews)</span>
        </div>
        
        {/* Backtest Metrics */}
        <div className="grid grid-cols-3 gap-2 mb-6 p-3 bg-white/5 rounded-lg border border-white/5">
          <div className="text-center">
            <div className="text-[10px] text-on-surface-variant uppercase tracking-tighter">Win Rate</div>
            <div className="font-data-mono text-secondary text-sm font-bold">{product.winRate}</div>
          </div>
          <div className="text-center border-x border-white/10">
            <div className="text-[10px] text-on-surface-variant uppercase tracking-tighter">DD</div>
            <div className="font-data-mono text-danger-red text-sm font-bold">{product.maxDrawdown}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-on-surface-variant uppercase tracking-tighter">Factor</div>
            <div className="font-data-mono text-primary text-sm font-bold">4.12</div>
          </div>
        </div>
        
        <div className="mt-auto flex items-center justify-between">
          <div>
            <div className="text-[12px] text-on-surface-variant line-through">${product.oldPrice}</div>
            <div className="text-headline-sm font-bold text-primary">${product.newPrice}</div>
          </div>
          <Link to={`/ea/${product.id}`} className="bg-primary text-on-primary-fixed font-bold px-6 py-2 rounded-lg hover:scale-105 transition-transform active:scale-95 inline-block text-center">
            View Details
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default MarketplaceCard;
